// src/pages/TeamManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";

import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Avatar,
  Modal,
  Form,
  message,
  Tooltip,
  Empty,
  Divider,
  Drawer,
  InputNumber,
  Switch,
  Popconfirm,
  List,
  Badge,
  Skeleton,
} from "antd";

import {
  TeamOutlined,
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  CrownOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  UsergroupAddOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";

import teamService from "@/services/teamService";
import apiClient from "@/utils/apiClient";

const { Title, Text } = Typography;
const { Option } = Select;

const TEAM_ROLES = [
  "advisor",
  "reservations",
  "transport",
  "accounts",
  "operations",
  "team_lead",
];

const DEPARTMENTS = [
  "Sales",
  "Operations",
  "Accounts",
  "Marketing",
  "Customer Support",
  "Transport",
  "Hotels",
  "Reservations",
];

const TeamManagement = () => {
  const [loading, setLoading] = useState(false);

  const [teams, setTeams] = useState([]);

  const [stats, setStats] = useState({
    totalTeams: 0,
    activeTeams: 0,
    totalMembers: 0,
    avgTeamSize: 0,
  });

  const [search, setSearch] = useState("");

  const [departmentFilter, setDepartmentFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const [selectedTeam, setSelectedTeam] = useState(null);

  const [assignableUsers, setAssignableUsers] = useState([]);

  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const [selectedMembers, setSelectedMembers] = useState([]);

  const [managerOptions, setManagerOptions] = useState([]);

  const [teamLeadOptions, setTeamLeadOptions] = useState([]);

  const [form] = Form.useForm();

const resetEditState = () => {

  setEditDrawerOpen(false);

  setSelectedTeam(null);

  setSelectedMembers([]);
};

  // =====================================================
  // HIERARCHY HELPERS
  // =====================================================

  const selectedManagerId =
    selectedTeam?.manager?._id || form.getFieldValue("manager");

  const selectedTeamLeadId =
    selectedTeam?.teamLead?._id || form.getFieldValue("teamLead");

  const usedUserIds = new Set(
    [
      selectedManagerId,
      selectedTeamLeadId,

      ...selectedMembers.map((m) => m._id),
    ].filter(Boolean),
  );

  const availableUsers = assignableUsers.filter(
    (user) => !usedUserIds.has(user._id),
  );


  // =====================================================
  // FETCH INITIAL
  // =====================================================

  useEffect(() => {
    fetchTeams();
    fetchStats();
    fetchAssignableUsers();
  }, []);

  // =====================================================
  // FETCH TEAMS
  // =====================================================

  const fetchTeams = async () => {
    try {
      setLoading(true);

      const response = await teamService.getAll();

      setTeams(response?.data || []);
    } catch (error) {
      console.error(error);

      message.error("Failed to fetch teams");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FETCH STATS
  // =====================================================

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/teams/stats");

      setStats(response?.data?.data || {});
    } catch (error) {
      console.error(error);
    }
  };

  // =====================================================
  // FETCH USERS
  // =====================================================

  const fetchAssignableUsers = async (query = "") => {
    try {
      setUserSearchLoading(true);

      const response = await apiClient.get(
        `/teams/assignable-users?search=${query}`,
      );

      const users = response?.data?.data || [];

      setAssignableUsers(users);

      const managers = users.filter((u) =>
        ["sales_manager", "team_lead"].includes(u.role),
      );

      setManagerOptions(managers);

      setTeamLeadOptions(managers);
    } catch (error) {
      console.error(error);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // =====================================================
  // DEBOUNCED USER SEARCH
  // =====================================================

  const debouncedUserSearch = useMemo(
    () => debounce(fetchAssignableUsers, 400),
    [],
  );

  // =====================================================
  // FILTERED TEAMS
  // =====================================================

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      const matchesSearch =
        !search || team.name?.toLowerCase().includes(search.toLowerCase());

      const matchesDepartment =
        !departmentFilter || team.department === departmentFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && team.isActive) ||
        (statusFilter === "inactive" && !team.isActive);

      return matchesSearch && matchesDepartment && matchesStatus;
    });
  }, [teams, search, departmentFilter, statusFilter]);

  // =====================================================
  // ADD MEMBER
  // =====================================================
  const addMember = (user) => {
    // prevent duplicate

    const alreadyExists = selectedMembers.some((m) => m._id === user._id);

    if (alreadyExists) {
      message.warning("User already added");

      return;
    }

    // prevent manager duplication

    if (selectedTeam?.manager?._id === user._id) {
      message.warning("Manager cannot be added as member");

      return;
    }

    // prevent team lead duplication

    if (selectedTeam?.teamLead?._id === user._id) {
      message.warning("Team lead cannot be added as member");

      return;
    }

    setSelectedMembers([
      ...selectedMembers,

      {
        ...user,

        teamRole: user.role,

        allocationPercentage: 100,
      },
    ]);
  };

  // =====================================================
  // REMOVE MEMBER
  // =====================================================

  const removeMember = (userId) => {
    setSelectedMembers(selectedMembers.filter((m) => m._id !== userId));
  };

  // =====================================================
  // UPDATE MEMBER FIELD
  // =====================================================

  const updateMemberField = (userId, field, value) => {
    setSelectedMembers(
      selectedMembers.map((member) =>
        member._id === userId
          ? {
              ...member,
              [field]: value,
            }
          : member,
      ),
    );
  };

  // =====================================================
  // CREATE TEAM
  // =====================================================

  const handleCreateTeam = async (values) => {
    try {
      setLoading(true);

      const payload = {
        ...values,

        members: selectedMembers.map((member) => ({
          user: member._id,

          role: member.teamRole,

          allocationPercentage: member.allocationPercentage,

          isActive: true,
        })),
      };

      const response = await teamService.create(payload);

      if (response?.success) {
        message.success("Team created successfully");

        setCreateModalOpen(false);

        form.resetFields();

        setSelectedMembers([]);

        fetchTeams();

        fetchStats();
      }
    } catch (error) {
      console.error(error);

      message.error(error?.response?.data?.message || "Failed to create team");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeam = async () => {
    try {
      if (!selectedTeam?._id) {
        return;
      }

      setLoading(true);

      const payload = {
        name: selectedTeam.name,

        description: selectedTeam.description || "",

        department: selectedTeam.department,

        manager: selectedTeam?.manager?._id || null,

        teamLead: selectedTeam?.teamLead?._id || null,

        isActive: selectedTeam.isActive !== false,

        members: selectedMembers.map((member) => ({
          user: member._id,

          role: member.teamRole || "advisor",

          allocationPercentage: Number(member.allocationPercentage || 100),

          isActive: member.isActive !== false,
        })),
      };

      const response = await teamService.update(selectedTeam._id, payload);

      if (response?.success) {
        message.success("Team updated successfully");

        resetEditState();

        await fetchTeams();

        await fetchStats();
      }
    } catch (error) {
      console.error("Update team error:", error);

      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update team",
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // DELETE TEAM
  // =====================================================

  const handleDeleteTeam = async (teamId) => {
    const previousTeams = [...teams];

    try {
      // optimistic update
      setTeams(teams.filter((team) => team._id !== teamId));

      await teamService.delete(teamId);

      message.success("Team deleted");

      fetchStats();
    } catch (error) {
      // rollback
      setTeams(previousTeams);

      console.error(error);

      message.error("Failed to delete team");
    }
  };

  return (
    <div
      style={{
        padding: 24,
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Row
        justify="space-between"
        align="middle"
        style={{
          marginBottom: 24,
        }}
      >
        <Col>
          <Title level={2}>
            <ApartmentOutlined
              style={{
                marginRight: 10,
                color: "#1677ff",
              }}
            />
            Team Management
          </Title>

          <Text type="secondary">Enterprise-grade multi-team management</Text>
        </Col>

        <Col>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Team
          </Button>
        </Col>
      </Row>

      {/* =====================================================
          STATS
      ===================================================== */}

      <Row
        gutter={16}
        style={{
          marginBottom: 24,
        }}
      >
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Teams"
              value={stats.totalTeams}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Active Teams"
              value={stats.activeTeams}
              valueStyle={{
                color: "#52c41a",
              }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Total Members"
              value={stats.totalMembers}
              prefix={<UsergroupAddOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Team Size"
              value={stats.avgTeamSize?.toFixed?.(1) || 0}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <Card
        style={{
          marginBottom: 24,
        }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="Search teams..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col span={6}>
            <Select
              style={{
                width: "100%",
              }}
              placeholder="Department"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              allowClear
            >
              {DEPARTMENTS.map((dept) => (
                <Option key={dept} value={dept}>
                  {dept}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={6}>
            <Select
              style={{
                width: "100%",
              }}
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Option value="active">Active</Option>

              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* =====================================================
    TEAM CARDS
===================================================== */}
      {loading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((item) => (
            <Col xs={24} md={12} xl={8} key={item}>
              <Card
                style={{
                  borderRadius: 16,
                }}
              >
                <Skeleton
                  active
                  avatar
                  paragraph={{
                    rows: 6,
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredTeams.map((team) => (
            <Col xs={24} md={12} xl={8} key={team._id}>
              <Card
                hoverable
                onClick={() => {
                  setSelectedTeam(team);

                  setSelectedMembers(
                    (team.members || [])

                      .filter((member) => member?.user?._id)

                      .map((member) => ({
                        _id: member.user._id,

                        name: member.user.name,

                        email: member.user.email,

                        role: member.user.role,

                        phone: member.user.phone,

                        teamRole: member.role || "advisor",

                        allocationPercentage:
                          member.allocationPercentage || 100,

                        isActive: member.isActive !== false,
                      })),
                  );

                  setEditDrawerOpen(true);
                }}
                style={{
                  borderRadius: 16,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                bodyStyle={{
                  minHeight: 320,
                }}
                actions={[
                  <Tooltip title="Edit Team">
                    <EditOutlined
                      onClick={(e) => {
                        e.stopPropagation();

                        setSelectedTeam(team);

                        setSelectedMembers(
                          (team.members || [])

                            .filter((member) => member?.user?._id)

                            .map((member) => ({
                              _id: member.user._id,

                              name: member.user.name,

                              email: member.user.email,

                              role: member.user.role,

                              phone: member.user.phone,

                              teamRole: member.role || "advisor",

                              allocationPercentage:
                                member.allocationPercentage || 100,

                              isActive: member.isActive !== false,
                            })),
                        );

                        setEditDrawerOpen(true);
                      }}
                    />
                  </Tooltip>,

                  <Popconfirm
                    title="Delete team?"
                    onConfirm={() => handleDeleteTeam(team._id)}
                  >
                    <DeleteOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{
                        color: "#ff4d4f",
                      }}
                    />
                  </Popconfirm>,
                ]}
              >
                <Space
                  direction="vertical"
                  style={{
                    width: "100%",
                  }}
                >
                  {/* HEADER */}

                  <Space
                    style={{
                      width: "100%",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                        }}
                      >
                        {team.name}
                      </Title>

                      <Text type="secondary">{team.slug}</Text>
                    </div>

                    <Tag color={team.isActive ? "green" : "red"}>
                      {team.isActive ? "Active" : "Inactive"}
                    </Tag>
                  </Space>

                  {/* DEPARTMENT */}

                  <Space wrap>
                    <Tag color="blue">{team.department}</Tag>

                    {team.performanceScore > 0 && (
                      <Tag color="purple">Score: {team.performanceScore}</Tag>
                    )}

                    {team.monthlyRevenue > 0 && (
                      <Tag color="gold">
                        ₹{team.monthlyRevenue?.toLocaleString()}
                      </Tag>
                    )}
                  </Space>

                  <Divider
                    style={{
                      margin: "10px 0",
                    }}
                  />

                  {/* MANAGER */}

                  <div>
                    <Text strong>
                      <CrownOutlined /> Manager
                    </Text>

                    <Space
                      style={{
                        marginTop: 8,
                      }}
                    >
                      <Avatar icon={<UserOutlined />} />

                      <div>
                        <Text strong>
                          {team?.manager?.name || "Not Assigned"}
                        </Text>

                        <br />

                        <Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {team?.manager?.email || ""}
                        </Text>
                      </div>
                    </Space>
                  </div>

                  {/* TEAM LEAD */}

                  <div>
                    <Text strong>Team Lead</Text>

                    <Space
                      style={{
                        marginTop: 8,
                      }}
                    >
                      <Avatar icon={<UserOutlined />} />

                      <div>
                        <Text strong>
                          {team?.teamLead?.name || "Not Assigned"}
                        </Text>

                        <br />

                        <Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {team?.teamLead?.email || ""}
                        </Text>
                      </div>
                    </Space>
                  </div>

                  <Divider />

                  {/* MEMBERS */}

                  <div>
                    <Space
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text strong>Members ({team?.members?.length || 0})</Text>

                      <Badge
                        count={team?.activeMemberCount || 0}
                        style={{
                          backgroundColor: "#52c41a",
                        }}
                      />
                    </Space>

                    <Avatar.Group
                      max={{
                        count: 6,
                      }}
                      style={{
                        marginTop: 14,
                      }}
                    >
                      {team?.members?.map((member) => (
                        <Tooltip
                          key={member?.user?._id}
                          title={
                            <div>
                              <div>
                                <strong>{member?.user?.name}</strong>
                              </div>

                              <div>{member?.role}</div>

                              <div>
                                Allocation: {member?.allocationPercentage}%
                              </div>
                            </div>
                          }
                        >
                          <Avatar
                            style={{
                              backgroundColor: "#1677ff",
                            }}
                          >
                            {member?.user?.name?.charAt(0)}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </Avatar.Group>
                  </div>

                  {/* FOOTER */}

                  <Divider />

                  <Row gutter={12}>
                    <Col span={8}>
                      <Statistic
                        title="Members"
                        value={team?.memberCount || 0}
                      />
                    </Col>

                    <Col span={8}>
                      <Statistic
                        title="Projects"
                        value={team?.activeProjects || 0}
                      />
                    </Col>

                    <Col span={8}>
                      <Statistic
                        title="Target"
                        value={team?.targetRevenue || 0}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* =====================================================
    EMPTY STATE
===================================================== */}

      {!filteredTeams.length && (
        <Card
          style={{
            marginTop: 24,
            borderRadius: 16,
          }}
        >
          <Empty description="No teams found" />
        </Card>
      )}

      {/* =====================================================
          CREATE TEAM MODAL
      ===================================================== */}

      <Modal
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);

          form.resetFields();

          setSelectedMembers([]);
        }}
        footer={null}
        width={1200}
        destroyOnClose
        title="Create Enterprise Team"
      >
        <Row gutter={24}>
          {/* LEFT */}
          <Col span={10}>
            <Form layout="vertical" form={form} onFinish={handleCreateTeam}>
              <Form.Item
                label="Team Name"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Department"
                name="department"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select>
                  {DEPARTMENTS.map((dept) => (
                    <Option key={dept} value={dept}>
                      {dept}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Manager" name="manager">
                <Select showSearch allowClear>
                  {managerOptions.map((user) => (
                    <Option key={user._id} value={user._id}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Team Lead" name="teamLead">
                <Select showSearch allowClear>
                  {teamLeadOptions.map((user) => (
                    <Option key={user._id} value={user._id}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Description" name="description">
                <Input.TextArea rows={4} />
              </Form.Item>

              <Form.Item
                label="Active Team"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
              >
                Create Team
              </Button>
            </Form>
          </Col>

          {/* RIGHT */}
          <Col span={14}>
            <Card title="Add Team Members">
              <Select
                showSearch
                style={{
                  width: "100%",
                  marginBottom: 20,
                }}
                placeholder="Search users..."
                loading={userSearchLoading}
                filterOption={false}
                onSearch={fetchAssignableUsers}
                onSelect={(value) => {
                  const user = assignableUsers.find((u) => u._id === value);

                  if (user) addMember(user);
                }}
              >
                {assignableUsers.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} ({user.role})
                  </Option>
                ))}
              </Select>

              <List
                dataSource={selectedMembers}
                locale={{
                  emptyText: "No members selected",
                }}
                renderItem={(member) => (
                  <List.Item>
                    <Card
                      style={{
                        width: "100%",
                        borderRadius: 12,
                      }}
                    >
                      <Row gutter={16} align="middle">
                        <Col span={8}>
                          <Space>
                            <Avatar>{member.name?.charAt(0)}</Avatar>

                            <div>
                              <Text strong>{member.name}</Text>

                              <br />

                              <Text type="secondary">{member.email}</Text>
                            </div>
                          </Space>
                        </Col>

                        <Col span={6}>
                          <Select
                            value={member.teamRole}
                            style={{
                              width: "100%",
                            }}
                            onChange={(value) =>
                              updateMemberField(member._id, "teamRole", value)
                            }
                          >
                            {TEAM_ROLES.map((role) => (
                              <Option key={role} value={role}>
                                {role}
                              </Option>
                            ))}
                          </Select>
                        </Col>

                        <Col span={6}>
                          <InputNumber
                            min={1}
                            max={100}
                            addonAfter="%"
                            value={member.allocationPercentage}
                            style={{
                              width: "100%",
                            }}
                            onChange={(value) =>
                              updateMemberField(
                                member._id,
                                "allocationPercentage",
                                value,
                              )
                            }
                          />

                          {member.allocationPercentage > 100 && (
                            <Text
                              type="danger"
                              style={{
                                fontSize: 12,
                                display: "block",
                                marginTop: 4,
                              }}
                            >
                              Overallocated
                            </Text>
                          )}
                        </Col>

                        <Col span={4}>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeMember(member._id)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Modal>

      {/* =====================================================
    EDIT TEAM DRAWER
===================================================== */}

      <Drawer
        title="Manage Team"
        open={editDrawerOpen}
        width={720}
        destroyOnClose
        onClose={resetEditState}
      >
        {selectedTeam && (
          <Space
            direction="vertical"
            size="large"
            style={{
              width: "100%",
            }}
          >
            {/* TEAM HEADER */}

            <Card
              style={{
                borderRadius: 16,
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                    }}
                  >
                    {selectedTeam.name}
                  </Title>

                  <Text type="secondary">{selectedTeam.department}</Text>
                </Col>

                <Col>
                  <Tag color={selectedTeam.isActive ? "green" : "red"}>
                    {selectedTeam.isActive ? "Active" : "Inactive"}
                  </Tag>
                </Col>
              </Row>
            </Card>

            {/* LEADERSHIP */}

            <Card
              title="Leadership"
              style={{
                borderRadius: 16,
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Manager</Text>

                  <Select
                    showSearch
                    allowClear
                    value={selectedTeam?.manager?._id}
                    style={{
                      width: "100%",
                      marginTop: 8,
                    }}
                    onChange={(value) => {
                      if (value && value === selectedTeam?.teamLead?._id) {
                        message.warning(
                          "Manager and Team Lead cannot be same user",
                        );
                        return;
                      }

                      if (selectedMembers.some((m) => m._id === value)) {
                        message.warning("Manager cannot exist in members");
                        return;
                      }

                      setSelectedTeam({
                        ...selectedTeam,
                        manager:
                          managerOptions.find((u) => u._id === value) || null,
                      });
                    }}
                  >
                    {managerOptions.map((user) => (
                      <Option key={user._id} value={user._id}>
                        {user.name} ({user.role})
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col span={12}>
                  <Text strong>Team Lead</Text>

                  <Select
                    showSearch
                    allowClear
                    value={selectedTeam?.teamLead?._id}
                    style={{
                      width: "100%",
                      marginTop: 8,
                    }}
                    onChange={(value) => {
                      if (value && value === selectedTeam?.manager?._id) {
                        message.warning(
                          "Manager and Team Lead cannot be same user",
                        );
                        return;
                      }

                      if (selectedMembers.some((m) => m._id === value)) {
                        message.warning("Team lead cannot exist in members");
                        return;
                      }

                      setSelectedTeam({
                        ...selectedTeam,
                        teamLead:
                          teamLeadOptions.find((u) => u._id === value) || null,
                      });
                    }}
                  >
                    {teamLeadOptions.map((user) => (
                      <Option key={user._id} value={user._id}>
                        {user.name} ({user.role})
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Card>

            {/* MEMBERS */}

            <Card
              title="Add Team Members"
              style={{
                borderRadius: 16,
              }}
            >
              <Select
                showSearch
                filterOption={false}
                style={{
                  width: "100%",
                  marginBottom: 20,
                }}
                placeholder="Search active users..."
                onSearch={fetchAssignableUsers}
                onSelect={(value) => {
                  const user = assignableUsers.find((u) => u._id === value);

                  if (!user) return;

                  if (user._id === selectedTeam?.manager?._id) {
                    message.warning("Manager cannot be member");
                    return;
                  }

                  if (user._id === selectedTeam?.teamLead?._id) {
                    message.warning("Team lead cannot be member");
                    return;
                  }

                  const alreadyExists = selectedMembers.some(
                    (m) => m._id === user._id,
                  );

                  if (alreadyExists) {
                    message.warning("User already added");
                    return;
                  }

                  addMember(user);
                }}
              >
                {availableUsers.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} ({user.role})
                  </Option>
                ))}
              </Select>

              <List
                dataSource={selectedMembers}
                locale={{
                  emptyText: "No members selected",
                }}
                renderItem={(member) => (
                  <List.Item>
                    <Card
                      size="small"
                      style={{
                        width: "100%",
                        borderRadius: 14,
                      }}
                    >
                      <Row gutter={16} align="middle">
                        <Col span={8}>
                          <Space>
                            <Avatar>{member.name?.charAt(0)}</Avatar>

                            <div>
                              <Text strong>{member.name}</Text>

                              <br />

                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                }}
                              >
                                {member.email}
                              </Text>
                            </div>
                          </Space>
                        </Col>

                        <Col span={6}>
                          <Select
                            value={member.teamRole}
                            style={{
                              width: "100%",
                            }}
                            onChange={(value) =>
                              updateMemberField(member._id, "teamRole", value)
                            }
                          >
                            {TEAM_ROLES.map((role) => (
                              <Option key={role} value={role}>
                                {role}
                              </Option>
                            ))}
                          </Select>
                        </Col>

                        <Col span={6}>
                          <InputNumber
                            min={1}
                            max={100}
                            addonAfter="%"
                            value={member.allocationPercentage}
                            style={{
                              width: "100%",
                            }}
                            onChange={(value) =>
                              updateMemberField(
                                member._id,
                                "allocationPercentage",
                                value,
                              )
                            }
                          />
                        </Col>

                        <Col span={4}>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeMember(member._id)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </List.Item>
                )}
              />
            </Card>

            <Button
              type="primary"
              size="large"
              block
              loading={loading}
              onClick={handleUpdateTeam}
            >
              Save Team Changes
            </Button>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default TeamManagement;
